import { useState, useMemo } from 'react';
import { Box, Typography, Chip, IconButton, Tab, Tabs, ToggleButtonGroup, ToggleButton, Stack, Divider, Table, TableBody, TableRow, TableCell, Paper, Tooltip, Button } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useProjectFull, useProjectRollup } from '../../hooks/useProjects';
import { useAuth } from '../../hooks/useAuth';
import { RollupCards, BarList } from './RollupCards';
import TimeTab from './TimeTab';
import ExpensesTab from './ExpensesTab';
import NotesTab from './NotesTab';
import BudgetTab from './BudgetTab';
import MembersTab from './MembersTab';
import LogHoursDialog from './LogHoursDialog';
import { KIND_COLOR, KIND_LABEL, STATUS_COLOR, kindIcon, formatHours, formatMoneyFull } from './constants';

export default function ProjectDetail({ projectId, flatById, onEdit }) {
  const { profile } = useAuth();
  const isExternal = !!profile?.is_external;
  const [tab, setTab] = useState(0);
  const [scope, setScope] = useState('subtree');
  const [logOpen, setLogOpen] = useState(false);
  const { data: full, isLoading } = useProjectFull(projectId);
  const { data: rollup, isLoading: rollupLoading } = useProjectRollup(projectId, scope);

  const breadcrumb = useMemo(() => {
    if (!full || !flatById) return [];
    const path = [];
    let cur = full;
    while (cur) {
      path.unshift(cur);
      cur = cur.parent_id ? flatById.get(cur.parent_id) : null;
    }
    return path;
  }, [full, flatById]);

  if (!projectId) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body1">Select a project from the tree to view its details.</Typography>
      </Box>
    );
  }
  if (isLoading || !full) {
    return <Box sx={{ p: 4 }}><Typography variant="body2">Loading…</Typography></Box>;
  }

  const KindIcon = kindIcon(full.kind);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <KindIcon sx={{ color: KIND_COLOR[full.kind] || 'text.secondary' }} />
          <Typography variant="h6" sx={{ flex: 1 }}>{full.name}</Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<AccessTimeIcon />}
            onClick={() => setLogOpen(true)}
            sx={{ mr: 1 }}
          >
            Log hours
          </Button>
          {!isExternal && (
            <Tooltip title="Edit / rename">
              <IconButton size="small" onClick={() => onEdit(full)}><EditIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mb: 0.5 }}>
          <Chip size="small" label={KIND_LABEL[full.kind] || full.kind} sx={{ bgcolor: KIND_COLOR[full.kind], color: '#fff' }} />
          <Chip size="small" label={full.status} sx={{ bgcolor: STATUS_COLOR[full.status], color: '#fff' }} />
          {full.starts_at && <Typography variant="caption" color="text.secondary">Starts {String(full.starts_at).slice(0, 10)}</Typography>}
          {full.ends_at && <Typography variant="caption" color="text.secondary">• Ends {String(full.ends_at).slice(0, 10)}</Typography>}
        </Box>
        {breadcrumb.length > 1 && (
          <Typography variant="caption" color="text.secondary">
            {breadcrumb.map((n, i) => (
              <span key={n.id}>
                {i > 0 && ' / '}
                {n.name}
              </span>
            ))}
          </Typography>
        )}
      </Box>

      {/* Scope toggle + tabs */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 1, gap: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ flex: 1, minHeight: 40 }} variant="scrollable">
          {isExternal ? [
            <Tab key="notes" label="Notes" sx={{ minHeight: 40 }} />,
            <Tab key="time" label="My Hours" sx={{ minHeight: 40 }} />,
          ] : [
            <Tab key="overview" label="Overview" sx={{ minHeight: 40 }} />,
            <Tab key="notes" label="Notes" sx={{ minHeight: 40 }} />,
            <Tab key="time" label="Time" sx={{ minHeight: 40 }} />,
            <Tab key="budget" label="Budget" sx={{ minHeight: 40 }} />,
            <Tab key="expenses" label="Expenses" sx={{ minHeight: 40 }} />,
            <Tab key="members" label="Members" sx={{ minHeight: 40 }} />,
            <Tab key="jobs" label={`Linked Jobs${full.linked_jobs?.length ? ` (${full.linked_jobs.length})` : ''}`} sx={{ minHeight: 40 }} />,
          ]}
        </Tabs>
        {!isExternal && (
          <ToggleButtonGroup size="small" value={scope} exclusive onChange={(_, v) => v && setScope(v)}>
            <ToggleButton value="self">Self</ToggleButton>
            <ToggleButton value="subtree">Subtree</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Tab content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {isExternal ? (
          <>
            {tab === 0 && <NotesTab projectId={projectId} readOnly />}
            {tab === 1 && <TimeTab projectId={projectId} scope="self" />}
          </>
        ) : (
          <>
            {tab === 0 && <OverviewPane full={full} rollup={rollup} rollupLoading={rollupLoading} />}
            {tab === 1 && <NotesTab projectId={projectId} />}
            {tab === 2 && <TimeTab projectId={projectId} scope={scope} />}
            {tab === 3 && <BudgetTab projectId={projectId} />}
            {tab === 4 && <ExpensesTab projectId={projectId} scope={scope} />}
            {tab === 5 && <MembersTab projectId={projectId} />}
            {tab === 6 && <LinkedJobsPane jobs={full.linked_jobs || []} />}
          </>
        )}
      </Box>

      <LogHoursDialog
        open={logOpen}
        onClose={() => setLogOpen(false)}
        defaultProjectId={full.kind === 'segment' ? projectId : undefined}
      />
    </Box>
  );
}

function OverviewPane({ full, rollup, rollupLoading }) {
  return (
    <Box>
      <RollupCards rollup={rollup} loading={rollupLoading} />

      <BarList
        title="Hours by person"
        rows={(rollup?.hours_by_person || []).map((r) => ({ label: r.person || '(unassigned)', value: r.hours }))}
        valueFormat={formatHours}
      />

      <BarList
        title="Expenses by category"
        rows={(rollup?.expenses_by_category || []).map((r) => ({ label: r.category || '(uncategorized)', value: r.amount }))}
        valueFormat={formatMoneyFull}
      />

      {full.children?.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Direct sub-projects ({full.children.length})</Typography>
          <Table size="small">
            <TableBody>
              {full.children.map((c) => (
                <TableRow key={c.id}>
                  <TableCell sx={{ py: 0.5 }}>{c.name}</TableCell>
                  <TableCell sx={{ py: 0.5 }}><Chip size="small" label={c.kind} sx={{ bgcolor: KIND_COLOR[c.kind], color: '#fff' }} /></TableCell>
                  <TableCell sx={{ py: 0.5 }}>{c.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {full.description && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Description</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{full.description}</Typography>
        </Paper>
      )}

      {(full.hero_image_url || full.gdrive_folder_url) && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Links</Typography>
          <Stack spacing={0.5}>
            {full.hero_image_url && <a href={full.hero_image_url} target="_blank" rel="noreferrer">Hero image ↗</a>}
            {full.gdrive_folder_url && <a href={full.gdrive_folder_url} target="_blank" rel="noreferrer">Gdrive folder ↗</a>}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}

function LinkedJobsPane({ jobs }) {
  if (jobs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
        No jobs linked to this project yet. Link from JobDetail by setting the Project field.
      </Typography>
    );
  }
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Table size="small">
        <TableBody>
          {jobs.map((j) => (
            <TableRow key={j.id} hover>
              <TableCell>{j.title || j.client || '(untitled)'}</TableCell>
              <TableCell>{j.status}</TableCell>
              <TableCell>{j.shoot_date ? String(j.shoot_date).slice(0, 10) : '—'}</TableCell>
              <TableCell align="right">{formatMoneyFull(j.revenue)}</TableCell>
              <TableCell sx={{ width: 36 }}>
                <IconButton size="small" component="a" href={`/jobs?jobId=${j.id}`}><OpenInNewIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
